<template>
  <AppShell>
    <section class="space-y-8">
      <div class="space-y-3">
        <p class="app-eyebrow">{{ t("purchase.eyebrow") }}</p>
        <h1 class="text-3xl font-bold tracking-tight">
          {{ t("purchase.title", { name: firstName }) }}
        </h1>
        <p class="max-w-2xl text-sm leading-7 text-brand-muted-strong">
          {{ t("purchase.description") }}
        </p>
      </div>

      <Card class="rounded-none border-brand-line bg-brand-surface shadow-none">
        <CardHeader class="hidden flex-row items-start justify-between p-8 pb-4">
          <div>
            <p class="app-eyebrow">{{ t("purchase.overview") }}</p>
            <CardTitle class="mt-2 text-xl font-bold">{{
              t("purchase.tokenStatus")
            }}</CardTitle>
          </div>
          <div
            class="flex size-10 items-center justify-center rounded-full bg-brand-surface-muted text-brand-ink"
          >
            <Coins class="size-5" />
          </div>
        </CardHeader>
        <CardContent class="space-y-8 p-8 pt-2">
          <div
            class="hidden grid gap-6 border-t border-brand-line py-6 md:grid-cols-3"
          >
            <TokenStat
              :label="t('purchase.total')"
              :value="0"
              highlighted
            />
            <TokenStat :label="t('purchase.locked')" :value="0" />
            <TokenStat :label="t('purchase.pending')" :value="0" />
          </div>

          <div class="space-y-4 border-b border-brand-line pb-6">
            <div class="flex items-end justify-between gap-4">
              <div class="space-y-1">
                <p class="app-eyebrow">{{ t("purchase.selectAmount") }}</p>
                <p class="text-xs text-brand-muted">
                  {{
                    t("purchase.amountRange", {
                      min: minAmount,
                      max: maxAmount,
                    })
                  }}
                </p>
              </div>
              <Input
                type="number"
                inputmode="numeric"
                :min="minAmount"
                :max="maxAmount"
                :model-value="purchaseAmount"
                :disabled="!limitsReady"
                class="h-12 w-28 rounded-none border-brand-line text-right text-base font-bold text-brand-ink shadow-none"
                @change="onAmountChange"
              />
            </div>
            <SliderRoot
              v-model="sliderValue"
              :min="minAmount"
              :max="maxAmount"
              :step="1"
              :disabled="!limitsReady"
              class="relative flex w-full touch-none select-none items-center py-2 data-disabled:opacity-50"
            >
              <SliderTrack
                class="relative h-1.5 w-full grow overflow-hidden rounded-full bg-brand-line"
              >
                <SliderRange class="absolute h-full bg-brand-fill" />
              </SliderTrack>
              <SliderThumb
                class="block size-4 shrink-0 rounded-full border border-brand-ink bg-brand-surface shadow-sm transition-shadow hover:ring-4 hover:ring-brand-ink/15 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-ink/20"
              />
            </SliderRoot>
          </div>

          <div v-if="false" class="grid gap-3 md:grid-cols-3">
            <Button
              class="h-12 rounded-none border-0 bg-brand-fill text-[11px] font-bold uppercase tracking-[0.14em] text-white shadow-none hover:bg-brand-fill-hover disabled:opacity-50"
              :disabled="checkoutStartDisabled"
              @click="startCheckout"
            >
              <Loader2 v-if="isStartingCheckout" class="size-4 animate-spin" />
              <TrendingUp v-else class="size-4" />
              {{ t("purchase.investNow") }}
            </Button>
            <Button
              class="h-12 rounded-none border-0 bg-brand-disabled-bg text-[11px] font-bold uppercase tracking-[0.14em] text-brand-disabled-fg shadow-none"
            >
              <Sparkles class="size-4" />
              {{ t("purchase.activateBenefits") }}
            </Button>
            <Button
              class="h-12 rounded-none border-0 bg-brand-disabled-bg text-[11px] font-bold uppercase tracking-[0.14em] text-brand-disabled-fg shadow-none"
            >
              <ArrowLeftRight class="size-4" />
              {{ t("purchase.transfer") }}
            </Button>
          </div>

          <div
            v-if="paymentError"
            class="border border-destructive/30 bg-destructive/5 p-4 text-sm font-semibold text-destructive"
          >
            {{ paymentError }}
          </div>

          <div class="space-y-3">
            <div class="flex items-center gap-3">
              <Checkbox id="agb-checkbox" v-model="agbAccepted" />
              <Label for="agb-checkbox" class="gap-1 cursor-pointer text-xs leading-5 text-brand-muted-strong font-normal">
                {{ t("purchase.agbPrefix") }}
                <button
                  type="button"
                  class="hidden font-semibold text-brand-ink underline underline-offset-2 hover:text-brand-ink-hover"
                  :disabled="documentLoading === 'agb'"
                  @click.stop="openDocument('agb')"
                >{{ t("purchase.agbLink") }}</button>
                <a
                    class="font-semibold text-brand-ink underline underline-offset-2 hover:text-brand-ink-hover"
                    :href="t('footer.links.terms.href')"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                  {{ t("footer.links.terms.labelInline") }}
                </a>
                {{ t("purchase.agbSuffix") }}
              </Label>
            </div>
            <div class="flex items-center gap-3">
              <Checkbox id="privacy-checkbox" v-model="privacyAccepted" />
              <Label for="privacy-checkbox" class="gap-1 cursor-pointer text-xs leading-5 text-brand-muted-strong font-normal">
                {{ t("purchase.privacyPrefix") }}
                <button
                  type="button"
                  class="hidden font-semibold text-brand-ink underline underline-offset-2 hover:text-brand-ink-hover"
                  :disabled="documentLoading === 'privacy'"
                  @click.stop="openDocument('privacy')"
                >{{ t("purchase.privacyLink") }}</button>
                <a
                    class="font-semibold text-brand-ink underline underline-offset-2 hover:text-brand-ink-hover"
                    :href="t('footer.links.privacy.href')"
                    target="_blank"
                    rel="noopener noreferrer"
                > {{ t("footer.links.privacy.label") }}
                </a>
                {{ t("purchase.privacySuffix") }}
              </Label>
            </div>
          </div>

          <div
            class="grid gap-4 border border-brand-line bg-brand-surface-muted p-5 md:grid-cols-[1fr_auto] md:items-center"
          >
            <div class="space-y-2">
              <p class="app-eyebrow">{{ t("purchase.cart") }}</p>
              <div class="text-sm font-semibold text-brand-ink">
                {{ t("purchase.tokenAmount", { amount: purchaseAmount }) }}
              </div>
              <div class="text-xs leading-6 text-brand-muted">
                {{
                  t("purchase.orderLine", {
                    reference: purchaseOrder.orderReference,
                    amount: formattedGrossAmount,
                    country: purchaseOrder.countryCode,
                  })
                }}
              </div>
            </div>
            <Button
              variant="outline"
              class="h-12 rounded-none border-brand-line bg-brand-surface px-6 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-ink shadow-none hover:bg-brand-surface-muted disabled:opacity-50"
              :disabled="isStartingCheckout || !limitsReady || !agbAccepted || !privacyAccepted"
              @click="openConfirmation"
            >
              <Loader2 v-if="isStartingCheckout" class="size-4 animate-spin" />
              <ShoppingCart v-else class="size-4" />
              {{
                isStartingCheckout
                  ? t("purchase.startingCheckout")
                  : t("purchase.startCheckout")
              }}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card class="rounded-none border-brand-line bg-brand-surface shadow-none">
        <CardHeader class="flex-row items-start justify-between gap-4 p-8 pb-4">
          <div>
            <p class="app-eyebrow">{{ t("purchase.paymentsEyebrow") }}</p>
            <CardTitle class="mt-2 text-xl font-bold">{{
              t("purchase.paymentsTitle")
            }}</CardTitle>
          </div>
          <Button
            variant="outline"
            size="icon"
            class="size-10 rounded-none border-brand-line bg-brand-surface text-brand-ink shadow-none hover:bg-brand-surface-muted"
            :disabled="paymentsLoading"
            :aria-label="t('purchase.refreshPayments')"
            :title="t('purchase.refreshPayments')"
            @click="loadPayments"
          >
            <Loader2 v-if="paymentsLoading" class="size-4 animate-spin" />
            <RefreshCw v-else class="size-4" />
          </Button>
        </CardHeader>
        <CardContent class="p-8 pt-2">
          <div
            v-if="paymentsError"
            class="mb-4 border border-destructive/30 bg-destructive/5 p-4 text-sm font-semibold text-destructive"
          >
            {{ paymentsError }}
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{{ t("purchase.paymentTable.order") }}</TableHead>
                <TableHead>{{ t("purchase.paymentTable.status") }}</TableHead>
                <TableHead class="text-right">{{
                  t("purchase.paymentTable.amount")
                }}</TableHead>
                <TableHead>{{ t("purchase.paymentTable.method") }}</TableHead>
                <TableHead>{{ t("purchase.paymentTable.eventDate") }}</TableHead>
                <TableHead>{{ t("purchase.paymentTable.expiresAt") }}</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow v-if="paymentsLoading">
                <TableCell colspan="7" class="py-8 text-center text-brand-muted">
                  {{ t("purchase.paymentsLoading") }}
                </TableCell>
              </TableRow>
              <TableRow v-else-if="visiblePayments.length === 0">
                <TableCell colspan="7" class="py-8 text-center text-brand-muted">
                  {{ t("purchase.paymentsEmpty") }}
                </TableCell>
              </TableRow>
              <template v-else>
                <TableRow v-for="payment in visiblePayments" :key="paymentKey(payment)">
                  <TableCell class="font-medium text-brand-ink">
                    <div>
                      {{ payment.orderId ?? payment.paymentProviderReference ?? "—" }}
                    </div>
                    <div class="text-xs font-normal text-brand-muted">
                      {{ payment.id ?? payment.paymentProviderSessionId ?? "" }}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge :variant="paymentStatusVariant(payment)">
                      {{ paymentStatusLabel(payment) }}
                    </Badge>
                  </TableCell>
                  <TableCell class="text-right font-medium">
                    <div>{{ formatPaymentGrossAmount(payment) }}</div>
                    <div
                      v-if="payment.amountInCents !== undefined"
                      class="text-xs font-normal text-brand-muted"
                    >
                      {{
                        t("purchase.paymentTable.amountBreakdown", {
                          net: formatPaymentNetAmount(payment),
                          tax: formatPaymentTaxAmount(payment),
                          rate: formatPaymentTaxRate(payment),
                        })
                      }}
                    </div>
                  </TableCell>
                  <TableCell class="text-brand-muted">
                    {{ paymentMethodLabel(payment) ?? "" }}
                  </TableCell>
                  <TableCell>
                    {{ formatPaymentDate(paymentEventDate(payment)) }}
                  </TableCell>
                  <TableCell>
                    {{ formatPaymentDate(payment.expiresAt) }}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </template>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog v-model:open="confirmOpen">
        <DialogScrollContent
          class="max-w-4xl gap-6 rounded-none border-brand-line p-8"
        >
          <div class="space-y-1">
            <p class="app-eyebrow">{{ t("purchase.confirm.title") }}</p>
            <p class="text-sm text-brand-muted">
              {{ t("purchase.confirm.subtitle") }}
            </p>
          </div>

          <div class="grid gap-6 md:grid-cols-2">
            <div class="space-y-4 bg-brand-surface-muted p-6">
              <p class="app-eyebrow">{{ t("purchase.confirm.personalData") }}</p>
              <dl class="space-y-3 text-sm">
                <div class="flex justify-between gap-4">
                  <dt class="font-semibold text-brand-ink">
                    {{ t("purchase.confirm.name") }}
                  </dt>
                  <dd class="text-right text-brand-muted-strong">
                    {{ customerName }}
                  </dd>
                </div>
                <div class="flex justify-between gap-4">
                  <dt class="font-semibold text-brand-ink">
                    {{ t("purchase.confirm.birthday") }}
                  </dt>
                  <dd class="text-right text-brand-muted-strong">
                    {{ customerBirthday }}
                  </dd>
                </div>
                <div class="flex justify-between gap-4">
                  <dt class="font-semibold text-brand-ink">
                    {{ t("purchase.confirm.address") }}
                  </dt>
                  <dd class="text-right text-brand-muted-strong">
                    <span
                      v-for="line in customerAddressLines"
                      :key="line"
                      class="block"
                      >{{ line }}</span
                    >
                  </dd>
                </div>
              </dl>
            </div>

            <div class="space-y-4 bg-brand-surface-muted p-6">
              <p class="app-eyebrow">{{ t("purchase.confirm.summary") }}</p>
              <dl class="space-y-3 text-sm">
                <div class="flex justify-between gap-4">
                  <dt class="font-semibold text-brand-ink">
                    {{ t("purchase.confirm.buyer") }}
                  </dt>
                  <dd class="text-right text-brand-muted-strong">
                    {{ customerName }}
                  </dd>
                </div>
                <div class="flex justify-between gap-4">
                  <dt class="font-semibold text-brand-ink">
                    {{ t("purchase.confirm.amount") }}
                  </dt>
                  <dd class="text-right text-brand-muted-strong">
                    {{ purchaseAmount }}
                  </dd>
                </div>
                <div class="flex justify-between gap-4">
                  <dt class="font-semibold text-brand-ink">
                    {{ t("purchase.confirm.pricePerToken") }}
                  </dt>
                  <dd class="text-right text-brand-muted-strong">
                    {{ formattedPricePerToken }}
                  </dd>
                </div>
                <div class="flex justify-between gap-4">
                  <dt class="font-semibold text-brand-ink">
                    {{ t("purchase.confirm.netAmount") }}
                  </dt>
                  <dd class="text-right text-brand-muted-strong">
                    {{ formattedNetAmount }}
                  </dd>
                </div>
                <div class="flex justify-between gap-4">
                  <dt class="font-semibold text-brand-ink">
                    {{
                      t("purchase.confirm.taxAmount", {
                        rate: formattedTaxRate,
                      })
                    }}
                  </dt>
                  <dd class="text-right text-brand-muted-strong">
                    {{ formattedTaxAmount }}
                  </dd>
                </div>
                <div
                  class="flex justify-between gap-4 border-t border-brand-line pt-3"
                >
                  <dt class="font-bold text-brand-ink">
                    {{ t("purchase.confirm.grossAmount") }}
                  </dt>
                  <dd class="text-right font-bold text-brand-ink">
                    {{ formattedGrossAmount }}
                  </dd>
                </div>
              </dl>
              <p class="text-xs leading-5 text-brand-muted">
                {{
                  t("purchase.confirm.paymentHint", {
                    provider: providerLabel,
                  })
                }}
              </p>
            </div>
          </div>

          <div class="flex items-start gap-3">
            <Checkbox
              id="us-tax-switch"
              v-model="usTaxConfirmation"
              class="mt-0.5"
            />
            <Label
              for="us-tax-switch"
              class="cursor-pointer text-xs leading-5 text-brand-muted-strong font-normal"
            >
              {{ t("purchase.confirm.usTax") }}
            </Label>
          </div>

          <div class="space-y-3 text-xs leading-5 text-brand-muted-strong">
            <p>
              {{ t("purchase.confirm.legalAgbIntro") }}
              <a
                :href="termsUrl"
                target="_blank"
                rel="noopener noreferrer"
                class="font-semibold text-brand-ink underline underline-offset-2 hover:text-brand-ink-hover"
                >{{ termsUrl }}</a
              >
              <template v-if="branding.whitepaperUrl">
                {{ t("purchase.confirm.legalWhitepaperMid") }}
                <a
                  :href="branding.whitepaperUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="font-semibold text-brand-ink underline underline-offset-2 hover:text-brand-ink-hover"
                  >{{ branding.whitepaperUrl }}</a
                >
              </template>.
            </p>
            <p>{{ t("purchase.confirm.legalConfirm") }}</p>
            <p>{{ t("purchase.confirm.legalVolatility") }}</p>
            <p>{{ t("purchase.confirm.legalAppropriateness") }}</p>
          </div>

          <div
            v-if="paymentError"
            class="border border-destructive/30 bg-destructive/5 p-4 text-sm font-semibold text-destructive"
          >
            {{ paymentError }}
          </div>

          <DialogFooter class="gap-3 sm:justify-between">
            <Button
              variant="outline"
              class="h-12 rounded-none border-brand-line bg-brand-surface px-6 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-ink shadow-none hover:bg-brand-surface-muted"
              :disabled="isStartingCheckout"
              @click="confirmOpen = false"
            >
              {{ t("purchase.confirm.cancel") }}
            </Button>
            <Button
              class="h-12 rounded-none border-0 bg-brand-fill px-6 text-[11px] font-bold uppercase tracking-[0.14em] text-white shadow-none hover:bg-brand-fill-hover disabled:opacity-50"
              :disabled="isStartingCheckout || !usTaxConfirmation"
              @click="startCheckout"
            >
              <Loader2 v-if="isStartingCheckout" class="size-4 animate-spin" />
              {{ t("purchase.confirm.submit") }}
            </Button>
          </DialogFooter>
        </DialogScrollContent>
      </Dialog>
    </section>
  </AppShell>
</template>

<script setup lang="ts">
import { computed, defineComponent, h, onMounted, ref } from "vue";
import {
  ArrowLeftRight,
  Coins,
  Loader2,
  RefreshCw,
  ShoppingCart,
  Sparkles,
  TrendingUp,
} from "lucide-vue-next";
import { SliderRange, SliderRoot, SliderThumb, SliderTrack } from "reka-ui";
import AppShell from "@/components/AppShell.vue";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogFooter,
  DialogScrollContent,
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "vue-sonner";
import { useApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { paymentProviderLabel, useConfig } from "@/lib/config";
import { useCustomerSession } from "@/features/customer-session";
import { useRouter } from "vue-router";
import { useI18n } from "@/lib/i18n";
import { getBranding } from "@/lib/branding";
import {
  createPaymentApi,
  type PortalPayment,
  type PaymentProcessInfo,
  type TokenPurchaseOrder,
} from "@/features/payment";

const router = useRouter();
const auth = useAuth();
const api = useApi();
const config = useConfig();
const branding = getBranding(config.branding);
const customerSession = useCustomerSession();
const paymentApi = createPaymentApi(api);
const { t } = useI18n();

const isStartingCheckout = ref(false);
const paymentError = ref<string | null>(null);
const agbAccepted = ref(false);
const privacyAccepted = ref(false);
const documentLoading = ref<"agb" | "privacy" | null>(null);
const confirmOpen = ref(false);
const usTaxConfirmation = ref(false);
const termsUrl = computed(() => t("footer.links.terms.href"));

async function openDocument(type: "agb" | "privacy"): Promise<void> {
  if (documentLoading.value) return;
  documentLoading.value = type;
  try {
    const path = type === "agb" ? "/api/portal/documents/agb" : "/api/portal/documents/privacy";
    const response = await api.fetch(path, { headers: { Accept: "*/*" } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  } catch {
    toast.error(t("purchase.documentLoadError"));
  } finally {
    documentLoading.value = null;
  }
}

// Werden aus /api/portal/payment/process abgeleitet.
const processInfo = ref<PaymentProcessInfo | null>(null);
const minAmount = ref(0);
const maxAmount = ref(0);
const purchaseAmount = ref(0);
const limitsReady = ref(false);
const payments = ref<PortalPayment[]>([]);
const paymentsLoading = ref(false);
const paymentsError = ref<string | null>(null);
const visiblePayments = computed(() =>
  payments.value.filter((payment) => !isPendingPayment(payment)),
);
const checkoutStartDisabled = computed(
  () => isStartingCheckout.value || !limitsReady.value || paymentsLoading.value,
);
const providerLabel = computed(() => paymentProviderLabel(config.payment.provider));

const sliderValue = computed<number[]>({
  get: () => [purchaseAmount.value],
  set: ([value]) => {
    purchaseAmount.value = clampAmount(value ?? minAmount.value);
  },
});

function clampAmount(value: number): number {
  if (!Number.isFinite(value)) return minAmount.value;
  return Math.min(maxAmount.value, Math.max(minAmount.value, Math.round(value)));
}

function onAmountChange(event: Event) {
  purchaseAmount.value = clampAmount(
    Number((event.target as HTMLInputElement).value),
  );
}

onMounted(() => {
  void loadProcessInfo();
  void loadPayments();
});

async function loadProcessInfo() {
  try {
    const info = await paymentApi.getProcessInfo();
    processInfo.value = info;
    minAmount.value = Math.round(
      (info.minimumAmountInMinorUnits / 100) * info.conversionFactor,
    );
    maxAmount.value = Math.round(
      (info.maximumAmountInMinorUnits / 100) * info.conversionFactor,
    );
    purchaseAmount.value = Math.min(3000, maxAmount.value);
    limitsReady.value = true;
  } catch (error) {
    paymentError.value =
      error instanceof Error ? error.message : t("purchase.processLoadError");
  }
}

async function loadPayments() {
  paymentsLoading.value = true;
  paymentsError.value = null;
  try {
    payments.value = await paymentApi.listPayments();
  } catch (error) {
    paymentsError.value =
      error instanceof Error ? error.message : t("purchase.paymentsLoadError");
  } finally {
    paymentsLoading.value = false;
  }
}

const customer = computed(() => customerSession.current);
const onboarding = computed(() => customer.value?.onboarding?.onboarding);
const customerName = computed(() => {
  const name = [onboarding.value?.firstName, onboarding.value?.lastName]
    .filter(Boolean)
    .join(" ");
  if (name) return name;
  return auth.state.user?.name ?? "Portal User";
});
const firstName = computed(() => customerName.value.split(" ")[0]);
const purchaseOrder = computed<TokenPurchaseOrder>(() => {
  // const customerId = customer.value?.id ?? auth.state.user?.id ?? "portal-user";
  const customerId = "3f7e2c94-ffac-475e-b814-48abd65d330d";
  const orderReference = `${branding.orderReferencePrefix}-${customerId}-${purchaseAmount.value}`;
  const externalId = "U-f5404395-1688-4665-ab07-e416f2bb803e";
  const factor = processInfo.value?.conversionFactor || 1;
  const amountInMinorUnits = Math.round((purchaseAmount.value / factor) * 100);
  const taxRate = processInfo.value?.taxRate ?? 0;

  return {
    customerId,
    externalId,
    orderReference,
    amountInMinorUnits,
    taxAmountInMinorUnits: Math.round(
      amountInMinorUnits * taxRateFraction(taxRate),
    ),
    taxRate,
    currency: processInfo.value?.currency ?? "EUR",
    countryCode: processInfo.value?.countryCode ?? "AT",
    returnUrl: new URL("/payment/return", window.location.origin).toString(),
    idempotencyKey: orderReference,
    usTaxConfirmation: usTaxConfirmation.value,
  };
});
const formattedNetAmount = computed(() =>
  formatCurrency(
    purchaseOrder.value.amountInMinorUnits,
    purchaseOrder.value.currency,
  ),
);
const formattedTaxAmount = computed(() =>
  formatCurrency(
    purchaseOrder.value.taxAmountInMinorUnits,
    purchaseOrder.value.currency,
  ),
);
const formattedGrossAmount = computed(() =>
  formatCurrency(
    purchaseOrder.value.amountInMinorUnits +
      purchaseOrder.value.taxAmountInMinorUnits,
    purchaseOrder.value.currency,
  ),
);
const formattedTaxRate = computed(() =>
  new Intl.NumberFormat("de-AT", {
    style: "percent",
    maximumFractionDigits: 2,
  }).format(taxRateFraction(purchaseOrder.value.taxRate)),
);
const formattedPricePerToken = computed(() => {
  const count = purchaseAmount.value;
  const perTokenMinor =
    count > 0 ? purchaseOrder.value.amountInMinorUnits / count : 0;
  return formatCurrency(perTokenMinor, purchaseOrder.value.currency);
});

function formatCurrency(amountInMinorUnits: number, currency: string): string {
  return new Intl.NumberFormat("de-AT", {
    style: "currency",
    currency,
  }).format(amountInMinorUnits / 100);
}

function taxRateFraction(rate: number): number {
  return rate > 1 ? rate / 100 : rate;
}
const customerBirthday = computed(() => {
  const value = onboarding.value?.birthDate;
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat("de-AT", { dateStyle: "medium" }).format(date);
});
const customerAddressLines = computed<string[]>(() => {
  const c = onboarding.value;
  if (!c) return [];
  const street = [c.streetName, c.houseNumber].filter(Boolean).join(" ");
  const cityLine = [c.postCode, c.city].filter(Boolean).join(" ");
  return [customerName.value, street, cityLine, c.country].filter(
    (line): line is string => Boolean(line && line.trim()),
  );
});

function openConfirmation() {
  if (isStartingCheckout.value || !limitsReady.value) return;
  if (!agbAccepted.value || !privacyAccepted.value) return;
  usTaxConfirmation.value = false;
  confirmOpen.value = true;
}

async function startCheckout() {
  if (checkoutStartDisabled.value || !usTaxConfirmation.value) return;
  paymentError.value = null;
  isStartingCheckout.value = true;

  try {
    const session = await paymentApi.createSession(purchaseOrder.value);
    router.push({
      name: "payment-checkout",
      state: { session: JSON.parse(JSON.stringify(session)) },
    });
  } catch (error) {
    const message =
      error instanceof Error ? error.message : t("purchase.checkoutError");
    paymentError.value = message;
    toast.error(message);
    isStartingCheckout.value = false;
  }
}

function paymentKey(payment: PortalPayment): string {
  return (
    payment.id ??
    payment.paymentProviderSessionId ??
    payment.orderId ??
    `${payment.amountInCents ?? "unknown"}-${payment.expiresAt ?? "pending"}`
  );
}

function formatPaymentGrossAmount(payment: PortalPayment): string {
  if (payment.amountInCents === undefined) {
    return t("purchase.paymentTable.missing");
  }
  return formatCurrency(
    payment.amountInCents + (payment.taxAmountInCents ?? 0),
    payment.currency ?? "EUR",
  );
}

function formatPaymentNetAmount(payment: PortalPayment): string {
  if (payment.amountInCents === undefined) {
    return t("purchase.paymentTable.missing");
  }
  return formatCurrency(payment.amountInCents, payment.currency ?? "EUR");
}

function formatPaymentTaxAmount(payment: PortalPayment): string {
  if (payment.taxAmountInCents === undefined) {
    return t("purchase.paymentTable.missing");
  }
  return formatCurrency(payment.taxAmountInCents, payment.currency ?? "EUR");
}

function formatPaymentTaxRate(payment: PortalPayment): string {
  if (payment.taxRate === undefined) {
    return t("purchase.paymentTable.missing");
  }
  return new Intl.NumberFormat("de-AT", {
    style: "percent",
    maximumFractionDigits: 2,
  }).format(taxRateFraction(payment.taxRate));
}

function formatPaymentDate(value?: string): string {
  if (!value) return t("purchase.paymentTable.missing");
  return new Intl.DateTimeFormat("de-AT", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(value));
}

function paymentEventDate(payment: PortalPayment): string | undefined {
  const info = payment.paymentInfo;
  if (!info) return undefined;

  const eventDate = stringValue(info.eventDate);
  if (eventDate) return eventDate;

  const notificationItems = info.notificationItems;
  if (!Array.isArray(notificationItems)) return undefined;

  for (const item of notificationItems) {
    if (!isRecord(item)) continue;
    const requestItem = item.NotificationRequestItem;
    if (!isRecord(requestItem)) continue;
    const nestedEventDate = stringValue(requestItem.eventDate);
    if (nestedEventDate) return nestedEventDate;
  }

  return undefined;
}

function stringValue(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value && typeof value === "object" && !Array.isArray(value));
}

function isSessionExpired(payment: PortalPayment): boolean {
  if (!payment.expiresAt) return false;
  return new Date(payment.expiresAt) < new Date();
}

function paymentStatusLabel(payment: PortalPayment): string {
  const { status } = payment;
  if (status === "SESSION_CREATED") {
    return isSessionExpired(payment)
      ? t("purchase.paymentStatus.SESSION_EXPIRED")
      : t("purchase.paymentStatus.SESSION_CREATED");
  }
  if (status === "SESSION_FINISHED") {
    return t("purchase.paymentStatus.SESSION_FINISHED");
  }
  if (status === "AUTHORISED") return t("purchase.paymentStatus.AUTHORISED");
  if (status === "REFUNDED") return t("purchase.paymentStatus.REFUNDED");
  if (status === "CANCELLED") return t("purchase.paymentStatus.CANCELLED");
  if (status === "FAILED") return t("purchase.paymentStatus.FAILED");
  return status ?? t("purchase.paymentStatus.unknown");
}

function paymentStatusVariant(
  payment: PortalPayment,
): "default" | "secondary" | "destructive" | "outline" {
  const { status } = payment;
  if (status === "AUTHORISED" || status === "REFUNDED") return "default";
  if (status === "FAILED" || status === "CANCELLED") return "destructive";
  if (status === "SESSION_CREATED" || status === "SESSION_FINISHED") {
    return isSessionExpired(payment) ? "outline" : "secondary";
  }
  return "outline";
}

function paymentMethodLabel(payment: PortalPayment): string | null {
  return payment.paymentMethod ?? null;
}

function isPendingPayment(payment: PortalPayment): boolean {
  return (
    payment.status === "SESSION_CREATED" ||
    payment.status === "SESSION_FINISHED"
  );
}

const TokenStat = defineComponent({
  props: {
    label: { type: String, required: true },
    value: { type: [String, Number], required: true },
    highlighted: { type: Boolean, default: false },
  },
  setup(props) {
    return () =>
      h("div", { class: "space-y-2" }, [
        h("div", { class: "app-eyebrow" }, props.label),
        h(
          "div",
          {
            class: [
              "font-bold tracking-tight",
              props.highlighted
                ? "text-3xl text-brand-ink"
                : "text-2xl text-brand-ink/60",
            ],
          },
          String(props.value),
        ),
      ]);
  },
});
</script>
